// BB.1 — Business Plan Schema (fonte canônica declarativa).
//
// Esta camada é *puramente derivada*: não introduz novas tabelas nem
// migrations. Ela reorganiza o que o `business_workspace` já guarda em
// unidades editorialmente coerentes ("seções do plano"), com labels em
// pt-BR, critérios determinísticos de completude e ponteiros para a
// decisão responsável (mission_id / decision_group).
//
// Escopo desta onda (BB.1): subconjunto essencial — as 9 seções que
// hoje aparecem em /membros/meu-negocio, alinhadas 1:1 com BlockKey.
// A estrutura completa de 41 seções (Resumo Executivo, Fluxo de Caixa,
// Riscos, etc.) é BB.3. O contrato foi desenhado para acomodar mais
// seções sem quebrar consumidores.

import type { BlockKey } from "@/lib/business-blocks";
import { BLOCKS, BLOCK_BY_KEY, blockFieldRows, hasBlockContent } from "@/lib/business-blocks";

export type SectionStatus =
  | "empty"       // nunca preenchida
  | "in_progress" // parcial ou com conteúdo mas abaixo do mínimo
  | "complete"    // atende ao critério mínimo
  | "attention"   // conteúdo suspeito (curto/placeholder/inconsistente)
  | "blocked";    // depende de outra seção ainda vazia

export type SectionGroup =
  | "fundamentos"
  | "oferta"
  | "modelo_economico"
  | "operacao"
  | "mercado_vendas"
  | "execucao";

export type FieldEcho = {
  key: string;
  label: string;
  value: string;
  long: boolean;
};

export type BusinessPlanSection = {
  /** Identificador estável — não mudar entre versões. */
  id: string;
  /** Chave curta usada no roteamento e no adaptador (alinhada com BlockKey em BB.1). */
  key: BlockKey;
  /** Título editorial em pt-BR. */
  title: string;
  /** Uma frase explicando o que a seção captura. */
  description: string;
  /** Grupo de navegação do plano (BB.3 usará para agrupar visualmente). */
  group: SectionGroup;
  /** Ordem canônica dentro do grupo. */
  order: number;
  /** Bloco jsonb em `business_workspace` que serve de fonte. */
  sourceBlock: BlockKey;
  /** Outras seções cujas respostas alimentam ou informam esta. */
  dependencies: BlockKey[];
  /** Campos obrigatórios para status = "complete". */
  requiredFields: string[];
  /** Campos opcionais mas recomendados (contam para "in_progress"). */
  optionalFields: string[];
  /** Comprimento mínimo aceitável para textos longos (evita "atetete"). */
  minLongTextLength: number;
  /** Tempo estimado do usuário para concluir esta seção. */
  estimatedMinutes: number;
  /** Explica por que a seção importa (usado como copy do CTA guiado). */
  whyItMatters: string;
};

// ---------- registro estático (BB.1: 9 seções essenciais) ----------
//
// A ordem representa o **fluxo canônico de construção**: cada seção
// depende das anteriores. O resolvedor usa esta ordem como fallback.

export const BUSINESS_PLAN_SECTIONS: BusinessPlanSection[] = [
  {
    id: "sec.model.v1",
    key: "model",
    title: "Modelo de negócio",
    description: "Como o negócio ganha dinheiro, o que entrega e para quem.",
    group: "fundamentos",
    order: 1,
    sourceBlock: "model",
    dependencies: [],
    requiredFields: ["revenue_model", "delivery_mode", "main_cost"],
    optionalFields: ["ticket_average", "value_promise", "promise"],
    minLongTextLength: 12,
    estimatedMinutes: 5,
    whyItMatters:
      "Define a lógica central do negócio. Sem isso, nicho, preço e canais ficam sem direção.",
  },
  {
    id: "sec.niche.v1",
    key: "niche",
    title: "Nicho",
    description: "O recorte específico de mercado onde o negócio vai atuar.",
    group: "fundamentos",
    order: 2,
    sourceBlock: "niche",
    dependencies: ["model"],
    requiredFields: ["niche_name"],
    optionalFields: ["segment", "why"],
    minLongTextLength: 16,
    estimatedMinutes: 3,
    whyItMatters:
      "O nicho direciona público, oferta, posicionamento, marca e canais.",
  },
  {
    id: "sec.audience.v1",
    key: "audience",
    title: "Público-alvo",
    description: "Para quem o negócio resolve um problema real.",
    group: "fundamentos",
    order: 3,
    sourceBlock: "audience",
    dependencies: ["model", "niche"],
    requiredFields: ["persona_name", "pain"],
    optionalFields: ["context", "age_range", "region"],
    minLongTextLength: 20,
    estimatedMinutes: 5,
    whyItMatters:
      "Quanto mais claro o público, mais fácil vender, comunicar e reter.",
  },
  {
    id: "sec.offer.v1",
    key: "offer",
    title: "Proposta de valor",
    description: "A promessa clara que diferencia o negócio da concorrência.",
    group: "oferta",
    order: 4,
    sourceBlock: "offer",
    dependencies: ["niche", "audience"],
    requiredFields: ["headline", "promise"],
    optionalFields: ["differentiators", "guarantee"],
    minLongTextLength: 24,
    estimatedMinutes: 5,
    whyItMatters:
      "É o que faz um cliente escolher o negócio em vez de outro.",
  },
  {
    id: "sec.products.v1",
    key: "products",
    title: "Produtos e serviços",
    description: "O que exatamente é entregue e em qual formato.",
    group: "oferta",
    order: 5,
    sourceBlock: "products",
    dependencies: ["offer", "audience"],
    requiredFields: ["core_product"],
    optionalFields: ["catalog", "upsell", "format"],
    minLongTextLength: 12,
    estimatedMinutes: 4,
    whyItMatters:
      "Sem clareza do que se vende, não há como precificar nem operar.",
  },
  {
    id: "sec.pricing.v1",
    key: "pricing",
    title: "Preços e margens",
    description: "Quanto o negócio cobra, quanto custa e quanto sobra.",
    group: "modelo_economico",
    order: 6,
    sourceBlock: "pricing",
    dependencies: ["model", "products"],
    requiredFields: ["price", "cost"],
    optionalFields: ["margin_pct", "payment_terms", "recurrence"],
    minLongTextLength: 0,
    estimatedMinutes: 6,
    whyItMatters:
      "Sem margem definida, o negócio pode vender e ainda assim quebrar.",
  },
  {
    id: "sec.brand.v1",
    key: "brand",
    title: "Marca",
    description: "Nome, identidade visual e tom de voz.",
    group: "mercado_vendas",
    order: 7,
    sourceBlock: "brand",
    dependencies: ["audience", "offer"],
    requiredFields: ["name"],
    optionalFields: ["tagline", "tone", "colors"],
    minLongTextLength: 0,
    estimatedMinutes: 5,
    whyItMatters:
      "A marca é a memória do cliente. Precisa combinar com público e oferta.",
  },
  {
    id: "sec.channels.v1",
    key: "channels",
    title: "Canais de aquisição",
    description: "Onde o negócio encontra e conversa com clientes.",
    group: "mercado_vendas",
    order: 8,
    sourceBlock: "channels",
    dependencies: ["audience", "model"],
    requiredFields: ["primary"],
    optionalFields: ["secondary", "budget"],
    minLongTextLength: 0,
    estimatedMinutes: 4,
    whyItMatters:
      "Escolher o canal errado é o motivo #1 de negócios travados em zero venda.",
  },
  {
    id: "sec.sales_plan.v1",
    key: "sales_plan",
    title: "Plano de vendas",
    description: "Como conquistar os primeiros clientes de forma concreta.",
    group: "execucao",
    order: 9,
    sourceBlock: "sales_plan",
    dependencies: ["channels", "offer"],
    requiredFields: ["first_clients", "goal_30d"],
    optionalFields: ["weekly_actions"],
    minLongTextLength: 24,
    estimatedMinutes: 6,
    whyItMatters:
      "Transforma o plano em ação: quem contatar, quando, com qual meta.",
  },
];

export const SECTION_BY_KEY: Record<BlockKey, BusinessPlanSection | undefined> =
  Object.fromEntries(BUSINESS_PLAN_SECTIONS.map((s) => [s.key, s])) as any;

/**
 * Mapa inverso: para cada seção, quais outras seções a declaram como dependência.
 * Uso: mostrar ao usuário "esta alteração pode atualizar X, Y, Z".
 */
export const IMPACTED_BY_SECTION: Record<BlockKey, BlockKey[]> = (() => {
  const map: Record<string, BlockKey[]> = {};
  for (const s of BUSINESS_PLAN_SECTIONS) map[s.key] = [];
  for (const s of BUSINESS_PLAN_SECTIONS) {
    for (const dep of s.dependencies) {
      if (!map[dep]) map[dep] = [];
      map[dep].push(s.key);
    }
  }
  return map as Record<BlockKey, BlockKey[]>;
})();

/** Rótulos editoriais dos capítulos (agrupam seções na home e no plano). */
export const CHAPTER_LABELS: Record<SectionGroup, string> = {
  fundamentos: "Fundamentos",
  oferta: "Oferta",
  modelo_economico: "Modelo econômico",
  operacao: "Operação",
  mercado_vendas: "Mercado e vendas",
  execucao: "Execução",
};

/** Ordem canônica dos capítulos. */
export const CHAPTER_ORDER: SectionGroup[] = [
  "fundamentos", "oferta", "modelo_economico",
  "operacao", "mercado_vendas", "execucao",
];

// ---------- labels editoriais em pt-BR ----------
//
// Overrides sobre os labels já declarados em BLOCKS[].fields.
// Bloqueia a exibição de chaves cruas (MAIN COST, VALUE PROMISE, etc.).

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  main_cost: "Principal custo do negócio",
  delivery_mode: "Forma de entrega",
  revenue_model: "Como o negócio ganha dinheiro",
  value_promise: "Promessa de valor",
  ticket_estimate: "Ticket médio estimado",
  ticket_average: "Ticket médio",
  value_hypothesis: "Hipótese de valor",
  promise: "Promessa central",
  niche_name: "Nicho escolhido",
  segment: "Segmento de mercado",
  why: "Por que este nicho",
  persona_name: "Persona (cliente-alvo)",
  pain: "Dor principal do cliente",
  context: "Contexto de compra",
  age_range: "Faixa etária",
  region: "Onde o cliente está",
  headline: "Frase principal",
  differentiators: "Diferenciais",
  guarantee: "Garantia oferecida",
  core_product: "Produto ou serviço principal",
  catalog: "Catálogo",
  upsell: "Adicional ou upsell",
  format: "Formato da entrega",
  price: "Preço praticado",
  cost: "Custo direto",
  margin_pct: "Margem estimada",
  payment_terms: "Condições de pagamento",
  recurrence: "Recorrência",
  name: "Nome da marca",
  tagline: "Tagline",
  tone: "Tom de voz",
  colors: "Paleta de cores",
  primary: "Canal principal",
  secondary: "Canais de apoio",
  budget: "Investimento mensal",
  first_clients: "Primeiros clientes-alvo",
  weekly_actions: "Ações semanais",
  goal_30d: "Meta em 30 dias",
  notes: "Anotações do usuário",
};

/**
 * Rótulo editorial em pt-BR para uma chave de campo do workspace.
 * Ordem de resolução: override → FieldSpec do BLOCKS → título humanizado.
 * Nunca retorna a chave em MAIÚSCULAS ou snake_case.
 */
export function editorialLabel(fieldKey: string): string {
  if (FIELD_LABEL_OVERRIDES[fieldKey]) return FIELD_LABEL_OVERRIDES[fieldKey];
  for (const b of BLOCKS) {
    const f = b.fields.find((x) => x.key === fieldKey);
    if (f) return f.label;
  }
  return fieldKey
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Re-export para consumidores fora deste módulo.
export { BLOCK_BY_KEY, blockFieldRows, hasBlockContent };
export type { BlockKey };
